import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import api from "../../api/client";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Plus, Briefcase, Users, Clock, MapPin } from "lucide-react";
import EmployerNavBar from "../../components/EmployerNavBar";
import { ROLE_CLASSES } from "../../constants/statusColors";

const STATUS_COLORS = {
  active: ROLE_CLASSES.active,
  closed: ROLE_CLASSES.neutral,
  filled: ROLE_CLASSES.closed,
};

export default function JobPositionsList() {
  const navigate = useNavigate();
  const { employerUser, employerToken, clearEmployerAuth } = useAuthStore();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    document.title = "Joborigo — Positions";
  }, []);

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  const fetchPositions = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await api.get(`/job-positions${params}`, authConfig);
      setPositions(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, [statusFilter]);

  const formatLocation = (p) => {
    const parts = [];
    if (p.location_city) parts.push(p.location_city);
    if (p.location_country) parts.push(p.location_country);
    if (p.is_remote) parts.push("Remote");
    return parts.join(" · ") || "—";
  };

  const formatSalary = (p) => {
    if (!p.salary_min && !p.salary_max) return null;
    const cur = p.salary_currency || "EUR";
    if (p.salary_min && p.salary_max) {
      return `${cur} ${p.salary_min.toLocaleString()} – ${p.salary_max.toLocaleString()}`;
    }
    return `${cur} ${(p.salary_min || p.salary_max).toLocaleString()}+`;
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleSignOut = () => {
    clearEmployerAuth();
    navigate("/employers/login");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <EmployerNavBar />

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-1">Positions</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {employerUser?.is_agency
                ? "Roles you're sourcing for. Each position has its own pipeline of candidates."
                : "Roles you're hiring for. Each position has its own pipeline of candidates."}
            </p>
          </div>
          <button
            onClick={() => navigate("/employers/positions/new")}
            className="flex items-center gap-2 px-4 py-2 bg-brand-violet text-white rounded-md hover:opacity-90 transition whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            New position
          </button>
        </div>

        {/* Status filter tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-white/10">
          {["all", "active", "closed", "filled"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                statusFilter === s
                  ? "border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : positions.length === 0 ? (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-dashed border-gray-200 dark:border-white/10 p-16 text-center">
            <div className="w-12 h-12 bg-brand-violet/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-6 h-6 text-brand-violet dark:text-brand-violet-on-dark" />
            </div>
            <p className="text-gray-700 dark:text-gray-200 font-medium mb-1">
              No positions yet
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Create your first position to start sourcing candidates.
            </p>
            <button
              onClick={() => navigate("/employers/positions/new")}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-violet text-white rounded-md hover:opacity-90 transition"
            >
              <Plus className="w-4 h-4" />
              Create first position
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {positions.map((p) => (
              <Link
                key={p.id}
                to={`/employers/positions/${p.id}`}
                className="block bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-5 hover:border-brand-violet/50 dark:hover:border-brand-violet-on-dark/50 transition"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold truncate">
                        {p.title}
                      </h3>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          STATUS_COLORS[p.status] || STATUS_COLORS.active
                        }`}
                      >
                        {p.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1.5">
                        <Briefcase className="w-3.5 h-3.5" />
                        {p.employment_type}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" />
                        {formatLocation(p)}
                      </span>
                      {formatSalary(p) && (
                        <span className="text-gray-500 dark:text-gray-400">
                          {formatSalary(p)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-sm">
                    <span className="flex items-center gap-1.5 text-brand-violet dark:text-brand-violet-on-dark font-medium">
                      <Users className="w-3.5 h-3.5" />
                      {p.submission_count}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      {formatDate(p.created_at)}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
